import lodashTemplate from "lodash/template";
import { ILookupFormattedRow, ILookupResponseRowValue, ILookupRowItem, ILookupSelect, ILookupTerm } from "./types";
import { ISchemaKey } from "helpers/product-collection";

/**
 * Helper function to extract relevant variables from SELECT clause
 * @param tableColumn
 * @param select
 * @returns
 */
const getSchemaKey = (tableColumn: string, select: ILookupSelect) => {
    const term = select.terms.find((it) => it.column.table_column === tableColumn);

    if (!term) {
        return undefined;
    }

    return {
        column: tableColumn,
        delimiter: term.string_aggr?.delimiter,
        schemaField: term.column.schema_column,
    };
};

/**
 * Transform Lookup results by mapping metadata informations with rows
 * @param response
 * @param select
 * @returns
 */
export const transformLookupResponse = (response: any, select?: { terms: ILookupTerm[] }): ILookupRowItem[] => {
    if (!response || !response.metadata || !response.rows) {
        return [];
    }

    return response.rows.map((row: { values: ILookupResponseRowValue[] }) =>
        response.metadata.row_type.fields.reduce((acc, schema, index) => {
            const { column, delimiter, schemaField } = getSchemaKey(schema.name, select);

            const value = Object.values(row.values[index])[0]?.value;

            return {
                ...acc,
                [column]: {
                    rawValue: value,
                    value: delimiter ? String(value).split(delimiter) : [value],
                    type: schema.type.code,
                    schemaName: schemaField,
                },
            };
        }, {})
    );
};

/**
 * Transform Lookup results based on lookup display defined by schema
 * @param schema
 * @param rows
 * @returns
 */
export const transformLookupRowsBySchema = (schema: ISchemaKey, rows: ILookupRowItem[]): ILookupFormattedRow[] => {
    const selectTerm = schema.lookup_plan.select.terms.find((term) => term.column.schema_column === schema.field_name);
    const formatTemplate = lodashTemplate(schema.lookup_plan.display.format, { variable: "lookup" });
    const dropdownFormatTemplate = lodashTemplate(
        schema.lookup_plan.display.dropdown_format ?? schema.lookup_plan.display.format,
        { variable: "lookup" }
    );

    return rows.map((row) => {
        const selectTermsData = schema.lookup_plan.select.terms.reduce((acc, term) => {
            acc[term.column.schema_column] = row[term.column.table_column].rawValue;
            return acc;
        }, {});

        return {
            selectColumns: selectTermsData,
            value: {
                raw: row[selectTerm.column.table_column].rawValue,
                formatted: formatTemplate(selectTermsData),
                dropdownFormatted: dropdownFormatTemplate(selectTermsData),
            },
        };
    });
};
